import React, { useState, useMemo } from 'react';
import './SessionLimitModal.css';

/**
 * SessionLimitModal Component
 *
 * Displayed when user attempts to login but has reached max concurrent sessions (3).
 * User selects a session to end; the parent retries login with replace_session_id
 * so NO separate auth token is needed.
 *
 * Props:
 * - activeSessions: Array of active session objects from 429 error
 * - maxAllowed: Max number of sessions allowed (typically 3)
 * - onEndAndContinue(sessionId): Parent ends this session via login API and retries
 * - onCancel: Callback when user cancels login
 */
const SessionLimitModal = ({ activeSessions, maxAllowed, onEndAndContinue, onCancel }) => {
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Find the session with the oldest last_activity_at
  const oldestSessionId = useMemo(() => {
    if (!activeSessions?.length) return null;
    return activeSessions.reduce((oldest, s) => {
      if (!oldest) return s;
      const oldestTime = oldest.last_activity_at ? new Date(oldest.last_activity_at) : new Date(0);
      const sTime = s.last_activity_at ? new Date(s.last_activity_at) : new Date(0);
      return sTime < oldestTime ? s : oldest;
    }, null)?.session_id ?? null;
  }, [activeSessions]);

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile': return '📱';
      case 'tablet': return '📲';
      case 'desktop':
      default: return '💻';
    }
  };

  const formatLastActivity = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const diffMs = Date.now() - new Date(timestamp).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleEndAndContinue = async (sessionId) => {
    if (!sessionId) {
      setError('Please select a session to end');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onEndAndContinue(sessionId);
    } catch (err) {
      setError(err.message || 'Failed to end session. Please try again.');
      setLoading(false);
    }
  };

  const handleEndSelected = () => handleEndAndContinue(selectedSession);
  const handleEndOldest = () => handleEndAndContinue(oldestSessionId);

  return (
    <div className="modal-overlay">
      <div className="session-limit-modal">
        <div className="modal-header">
          <h2>⚠️ Session Limit Reached</h2>
          <button className="close-btn" onClick={onCancel} disabled={loading}>×</button>
        </div>

        <div className="modal-body">
          <p className="limit-message">
            You have reached the maximum of <strong>{maxAllowed} concurrent sessions</strong>.
            To continue, please select a session to end:
          </p>

          {error && <div className="error-message">{error}</div>}

          <div className="sessions-list">
            {activeSessions.map((session) => (
              <label
                key={session.session_id}
                className={`session-option ${selectedSession === session.session_id ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="session"
                  value={session.session_id}
                  checked={selectedSession === session.session_id}
                  onChange={() => setSelectedSession(session.session_id)}
                  disabled={loading}
                />
                
                <div className="session-content">
                  <div className="session-icon">
                    {getDeviceIcon(session.device_type)}
                  </div>
                  
                  <div className="session-details">
                    <div className="device-name">
                      {session.device_name || 'Unknown Device'}
                      {session.session_id === oldestSessionId && (
                        <span className="oldest-badge">oldest</span>
                      )}
                    </div>
                    <div className="device-info">
                      {session.browser && <span>{session.browser}</span>}
                      {session.os && <span> • {session.os}</span>}
                    </div>
                    <div className="activity-info">
                      Last active: {formatLastActivity(session.last_activity_at)}
                      {session.city && session.country && (
                        <span> • {session.city}, {session.country}</span>
                      )}
                    </div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn-end-oldest"
            onClick={handleEndOldest}
            disabled={loading || !oldestSessionId}
            title="End the least recently used session"
          >
            {loading ? 'Ending...' : 'End Oldest & Continue'}
          </button>
          <button
            className="btn-end-continue"
            onClick={handleEndSelected}
            disabled={!selectedSession || loading}
          >
            {loading ? 'Ending...' : 'End Selected & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionLimitModal;

